"use client";

import { useParams, usePathname, useRouter, notFound } from "next/navigation";
import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import {
  useProductBySize,
  useProductDetails,
  useProductGetColors,
  useProductGetVariant,
} from "@/data/product-details";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@repo/ui/components/ui/carousel";
import { Button } from "@repo/ui/components/ui/button";
import Icon from "@repo/ui/components/ui/icon/icon";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@repo/ui/components/ui/drawer";
import { useMediaQuery } from "@repo/ui/lib/use-media-query-hook";
import { cn } from "@repo/ui/lib/utils";
import { Separator } from "@repo/ui/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/ui/select";
import {
  ProductComposition,
  Variant,
  VariantColor,
} from "@/types/product-details";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import SizeGuide from "@components/size-guide";
import { useSizeGuide } from "@/data/sizeguide";
import { SizeGuideType } from "@/types/sizeguide";
import { useCategory, useCategoryPath } from "@/data/categories";
import { pushEcommerceEvent } from "@/lib/data-layer";
import { BreadcrumbManaged } from "@repo/ui/components/ui/breadcrumb";
import Link from "next/link";
import { IconButton } from "@repo/ui/components/ui/icon-button";
import { useSwipeable } from "react-swipeable";
import { Footer } from "@components/footer";
import ProductDescription from "@/(public)/product/components/product-description";
import { Price } from "@components/price";
import { sanifyProductTitle, sizesSort } from "@repo/next-shared/utils";
import Wishlist from "@components/wishlist";
import { useCurrentLocale } from "@repo/next-shared/hooks/useCurrentLocale";
import NormalizedSize from "@components/normalized-size";
import { brandSearchUrl } from "@/algolia/search-url";

export default function ProductPage() {
  const productId: string = useParams().id as string;
  const router = useRouter();
  const ignoreRoutes = ["/login", "/logout"];
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const t = useTranslations("Product");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sizesOpen, setSizesOpen] = useState(false);
  const [mobilePreventForward, setMobilePreventForward] = useState(true);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  const { data: session } = useSession();
  const { data: product } = useProductDetails(productId);
  const { data: variantsColors } = useProductGetColors(productId);
  const { data: variant } = useProductGetVariant(productId);
  const { data: variantsBySize } = useProductBySize(productId);
  const { data: sizeguides } = useSizeGuide();
  const { data: categoryPath } = useCategoryPath(product?.categoryId);
  const { data: category } = useCategory(product?.categoryId ?? "");

  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState(variant?.color);
  const [selectedVariant, setSelectedVariant] = useState(variant);

  if (!product) {
    notFound();
  }

  const handlers = useSwipeable({
    onSwipedUp: () => setDrawerOpen(true),
  });

  const breadcrumbItems = useMemo(() => {
    if (categoryPath && product) {
      return [
        {
          text: t("breadcrumb-home-link"),
          href: "/",
        },
        ...(categoryPath &&
          categoryPath.map((cat: any) => ({
            text: cat.title,
            href: `/categories/${cat.path}/${sanifyProductTitle(cat.title)}`,
          }))),
        {
          text:
            product.title.slice(0, 50) +
            (product.title.length > 30 ? "..." : ""),
        },
      ];
    }
    return [];
  }, [product, categoryPath]);

  const scrollDrawerTo = (to?: string, offset?: number): void => {
    if (!drawerRef?.current) return;

    let scrollOffset = 0;

    if (to) {
      const targetElement = drawerRef.current.querySelector(`#${to}`);
      if (targetElement) {
        const { top } = (targetElement as HTMLElement).getBoundingClientRect();
        const drawerTop = drawerRef.current.getBoundingClientRect().top;
        scrollOffset =
          top - drawerTop + drawerRef.current.scrollTop - (offset || 0);
      }
    }

    drawerRef.current.scrollTo({ top: scrollOffset, behavior: "smooth" });
  };

  function goToOfferPage() {
    router.push(
      `/make-an-offer/${product.id}/${selectedVariant.ean}/${sanifyProductTitle(product?.title)}`,
    );
  }

  function onShare() {
    if (window && "share" in navigator) {
      navigator
        .share({
          title: `iPriceIT - ${product.title}`,
          url: window?.location?.href,
        })
        .catch(() => console.log("Share canceled"));
    }
  }

  function isMobilePreventForward() {
    return mobilePreventForward;
  }

  function canShare() {
    try {
      return !!window && "share" in navigator;
    } catch {
      return false;
    }
  }

  function mobileForceSelectSize(openSizes: boolean = false) {
    setDrawerOpen(true);
    setSelectedSize(null);

    if (openSizes) {
      setTimeout(() => setSizesOpen(true), 250);
    }
  }

  function setFirstAvailableColorBySize() {
    let firstAvailableColor = null;

    if (variantsBySize && selectedSize && variantsBySize[selectedSize]) {
      firstAvailableColor = variantsBySize[selectedSize]?.find(
        (v: Variant) => v.available,
      );
    }

    if (firstAvailableColor) {
      setSelectedColor(firstAvailableColor?.color);
    } else {
      setSelectedColor(null);
      // setSelectedVariant(null);
    }
  }

  useEffect(() => {
    if (variant && !mobilePreventForward) {
      setSelectedSize(variant.size);
    }
  }, [variant]);

  useEffect(() => {
    if (
      variantsBySize &&
      selectedSize &&
      variantsBySize[selectedSize]?.find(
        (v: Variant) => v.color.name === selectedColor?.name,
      )?.available
    ) {
      setSelectedVariant(
        variantsBySize[selectedSize].find(
          (v: Variant) => v.color.name === selectedColor?.name,
        ),
      );
    } else {
      setFirstAvailableColorBySize();
    }
  }, [selectedColor, selectedSize]);

  useEffect(() => {
    if (
      variantsBySize &&
      selectedSize &&
      variantsBySize[selectedSize] &&
      !variantsBySize[selectedSize].find(
        (v: Variant) => v.color.name === selectedColor?.name,
      ) &&
      variantsBySize[selectedSize][0]
    )
      setSelectedColor(variantsBySize[selectedSize][0].color);
  }, [selectedSize]);

  useEffect(() => {
    if (!product || !selectedVariant) return;

    pushEcommerceEvent({
      event: "view_item",
      ecommerce: {
        currency: "EUR",
        value: selectedVariant.targetPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            item_brand: product.brand,
            item_category: category?.title,
            item_variant: `${selectedVariant.color.name},${selectedVariant.size}`,
            quantity: 1,
            price: selectedVariant.targetPrice,
          },
        ],
      },
    });
  }, [selectedVariant]);

  const productContent = (
    <div className="@container">
      <p className="md:hidden text-secondary-gray6 mt-6 max-md:text-sm">
        Rif. {selectedVariant?.ean} | {selectedVariant?.color.name}
      </p>
      <div id="size-select" className="mt-6">
        <h5 className="font-semibold mb-4">{t("size")}</h5>
        <div className="flex max-md:flex-col gap-2">
          <Select
            value={selectedSize || ""}
            open={sizesOpen}
            onOpenChange={setSizesOpen}
            onValueChange={(val) => {
              setSelectedSize(val);
              setMobilePreventForward(false);
            }}
          >
            <SelectTrigger
              variant="rounded"
              className="w-80 overflow-x-visible"
            >
              <SelectValue placeholder={t("select-size")} />
            </SelectTrigger>
            <SelectContent
              align="end"
              variant="rounded"
              className="max-h-[380px] max-md:max-h-[250px] z-50"
            >
              {variantsBySize &&
                Object.keys(variantsBySize) &&
                sizesSort(Object.keys(variantsBySize)).map((size, i) => {
                  return (
                    <Fragment key={i}>
                      <SelectItem
                        disabled={
                          !variantsBySize[size].find(
                            (v: Variant) => v.available,
                          )
                        }
                        variant="rounded"
                        className="font-bold data-[disabled]:font-normal"
                        value={size}
                      >
                        {variantsBySize[size][0].normalizedSize || size}
                      </SelectItem>
                      {i < Object.keys(variantsBySize).length - 1 && (
                        <SelectSeparator />
                      )}
                    </Fragment>
                  );
                })}
            </SelectContent>
          </Select>
          {selectedSize && (
            <NormalizedSize
              normalizedSize={selectedVariant.normalizedSize}
              size={selectedSize}
            />
          )}
        </div>
      </div>
      <a
        href="#"
        aria-label="size-guide link"
        className="text-sm block w-fitcursor-pointer text-secondary-gray6 underline mt-6"
      >
        {sizeguides &&
          sizeguides?.find(
            (guide: SizeGuideType) => guide.id === product?.sizeGuideId,
          ) && (
            <SizeGuide
              data={
                sizeguides.find(
                  (guide: SizeGuideType) => guide.id === product?.sizeGuideId,
                )!
              }
            >
              {t("size-guide")}
            </SizeGuide>
          )}
      </a>
      <Separator className="dark:max-md:bg-primary-black/10 mt-6 bg-primary-black/10 h-[2px] max-md:h-[1px]" />
      <div className="relative w-full">
        <h3 className="mt-6 max-md:text-base">{t("color")}</h3>
        <div className="flex w-full gap-4 mt-4 overflow-x-auto items-start">
          {variantsColors &&
            variantsColors.map(
              (color: VariantColor & { imgUrl: string }, i: number) => {
                return (
                  <button
                    key={i}
                    disabled={
                      selectedSize &&
                      variantsBySize[selectedSize] &&
                      !variantsBySize[selectedSize].find(
                        (el: Variant) =>
                          el.color.name === color.name && el.available,
                      )
                    }
                    onClick={() => {
                      setSelectedColor(color);
                    }}
                  >
                    <div
                      className={cn(
                        "flex flex-col items-center",
                        ((selectedSize &&
                          variantsBySize[selectedSize] &&
                          !variantsBySize[selectedSize].find(
                            (el: Variant) =>
                              el.color.name === color.name && el.available,
                          )) ||
                          isMobilePreventForward()) &&
                          "opacity-50",
                      )}
                    >
                      <div
                        className={cn(
                          `relative aspect-[16/9] w-[105px] border-secondary-gray1 overflow-hidden rounded-[10px]`,
                          `${selectedVariant?.color.name === color.name && selectedVariant?.available ? "border-primary-black dark:border-primary-magenta border-[3px]" : "border md:dark:border-none"}`,
                        )}
                      >
                        <Image
                          src={color.imgUrl}
                          sizes={"105px"}
                          className="w-[100%] object-cover data-[loaded=false]:animate-pulse data-[loaded=false]:bg-secondary-gray3/100 dark:data-[loaded=false]:bg-secondary-gray3/10"
                          alt={`${product.title}-${color.name}`}
                          fill
                        />
                      </div>
                      <div className="mt-2 rounded-full flex items-center justify-center">
                        {color.name}
                      </div>
                    </div>
                  </button>
                );
              },
            )}
        </div>
      </div>
      <Separator className="max-md:dark:bg-primary-black/10 mt-6 h-[2px] max-md:h-[1px]" />
      <div>
        <h3 className="max-md:font-semibold py-7 max-md:py-6">
          {t("useful-info")}
        </h3>
      </div>
      <Separator className="max-md:dark:bg-primary-black/10 h-[2px] max-md:h-[1px]" />
      <ProductDescription features={product?.productFeature} />
      <Separator className="max-md:dark:bg-primary-black/10 h-[2px] max-md:h-[1px] mt-7 max-md:mt-6" />

      {product?.description && (
        <>
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1">
              <div className="py-[30px]">
                <AccordionTrigger className="max-md:text-base font-medium py-0">
                  {t("features")}
                </AccordionTrigger>
                <AccordionContent className="mt-[20px] pb-0 opacity-50 md:dark:opacity-100 md:dark:text-secondary-gray6 font-medium">
                  {product?.description}
                  <br />
                  <br />
                  {product?.madeIn ? t("made-in") + product?.madeIn : ""}
                </AccordionContent>
              </div>
            </AccordionItem>
          </Accordion>
          <Separator className="max-md:dark:bg-primary-black/10 h-[2px] max-md:h-[1px]" />
        </>
      )}
      {product &&
        (product.productComposition?.length ? (
          <>
            <Accordion type="single" collapsible>
              <AccordionItem value="item-1">
                <div className="py-[30px]">
                  <AccordionTrigger className="max-md:text-base font-medium py-0">
                    {t("composition")}
                  </AccordionTrigger>
                  <AccordionContent className="mt-[20px] pb-0 opacity-50 md:dark:opacity-100 md:dark:text-secondary-gray6 font-medium">
                    <div className="flex flex-col gap-4">
                      <ul className="pl-5 list-disc">
                        {product?.productComposition?.map(
                          (composition: ProductComposition, i: number) => {
                            return (
                              <li key={i}>
                                <div className="flex gap-1">
                                  <p className="text-sm">
                                    {composition.percentage}%
                                  </p>
                                  <p className="text-sm">
                                    {composition.material}
                                  </p>
                                </div>
                              </li>
                            );
                          },
                        )}
                      </ul>
                    </div>
                  </AccordionContent>
                </div>
              </AccordionItem>
            </Accordion>
            <Separator className="max-md:dark:bg-primary-black/10 h-[2px] max-md:h-[1px]" />
          </>
        ) : (
          <></>
        ))}
      <Accordion type="single" collapsible>
        <AccordionItem value="item-1">
          <div className="py-[30px]">
            <AccordionTrigger className="max-md:text-base font-medium py-0">
              {t("return-info")}
            </AccordionTrigger>
            <AccordionContent className="mt-[20px] pb-0 opacity-50 md:dark:opacity-100 md:dark:text-secondary-gray6 font-medium">
              <p>{t("return-info-content")}</p>
            </AccordionContent>
          </div>
        </AccordionItem>
      </Accordion>
      <Separator className="max-md:dark:bg-primary-black/10 h-[2px] max-md:h-[1px]" />
      <Accordion type="single" collapsible>
        <AccordionItem value="item-1">
          <div className="py-[30px]">
            <AccordionTrigger className="max-md:text-base font-medium py-0">
              {t("other-features")}
            </AccordionTrigger>
            <AccordionContent className="mt-[20px] pb-0 opacity-50 md:dark:opacity-100 md:dark:text-secondary-gray6 font-medium">
              <ul>
                {product?.productWearability && (
                  <li className="max-md:text-sm">
                    <span className="font-semibold max-md:text-sm">
                      {t("wearability")}:
                    </span>{" "}
                    {product.productWearability}
                  </li>
                )}
                {product?.washingInstructions && (
                  <li className="max-md:text-sm">
                    <span className="font-semibold">
                      {t("washing-instructions")}:
                    </span>{" "}
                    {product.washingInstructions}
                  </li>
                )}
                {product?.gender && (
                  <li className="max-md:text-sm">
                    <span className="font-semibold max-md:text-sm">
                      {t("gender")}:
                    </span>{" "}
                    {product.gender}
                  </li>
                )}
              </ul>
            </AccordionContent>
          </div>
        </AccordionItem>
      </Accordion>
      <Separator className="max-md:dark:bg-primary-black/10 h-[2px] max-md:h-[1px]" />
      <div className="mt-6 text-center">
        <Link
          href={{
            pathname: "/help/contact",
            query: { topic: "topic-option-tech-problems", pid: productId },
          }}
          className="underline text-xl max-md:text-[18px]"
        >
          {t("report-problems")}
        </Link>
      </div>
      <div className="w-full max-md:w-auto flex gap-5 items-center py-10">
        <div className="text-xl max-md:text-[18px] max-md:font-[600]">
          {t("doubts")}
        </div>
        <Link
          href={{
            pathname: "/help/contact",
            query: { topic: "topic-option-product", pid: productId },
          }}
        >
          <Icon
            icon="Mail"
            className="w-[50px] h-[35px] max-md:w-[40px] max-md:h-[28px]"
          />
        </Link>
        {session && (
          <Link href="https://wa.me/393519546854" target="_blank">
            <Icon
              className="w-[50px] h-[50px] max-md:w-[40px] max-md:h-[40px] fill-[#25D366]"
              icon="Whatsapp"
            />
          </Link>
        )}
      </div>
    </div>
  );

  /* DESKTOP */
  if (isDesktop) {
    return (
      <div>
        <BreadcrumbManaged items={breadcrumbItems} />
        <div className="grid grid-cols-2 gap-[80px] pt-[60px]">
          <div className="relative">
            {selectedVariant && selectedVariant.imgUrl ? (
              <Carousel
                className="rounded-none"
                autoplay={false}
                thumb={true}
                orientation="vertical"
              >
                <CarouselContent className="h-[680px]">
                  {selectedVariant.imgUrl.map((img: any, i: number) => {
                    return (
                      <CarouselItem
                        key={i}
                        className="bg-center bg-contain bg-no-repeat"
                        style={{
                          backgroundImage: `url(${img})`,
                          scale: "0.95",
                        }}
                      ></CarouselItem>
                    );
                  })}
                </CarouselContent>
              </Carousel>
            ) : selectedVariant && selectedVariant.catalogImgUrl ? (
              <div className="relative w-[100%] aspect-[16/10] overflow-hidden rounded-lg shadow-catalog-image dark:shadow-none">
                <Image
                  data-loaded="false"
                  className="w-[100%] object-cover data-[loaded=false]:animate-pulse data-[loaded=false]:bg-secondary-gray3/100 dark:data-[loaded=false]:bg-secondary-gray3/10"
                  src={selectedVariant.catalogImgUrl}
                  fill
                  sizes="(min-width: 768px) 50vw, 100vw"
                  style={{
                    objectFit: "cover",
                  }}
                  onLoad={(event) => {
                    event.currentTarget.setAttribute("data-loaded", "true");
                  }}
                  alt={product?.title}
                />
              </div>
            ) : (
              <div></div>
            )}
          </div>
          <div>
            <Link href={brandSearchUrl(product?.brand)}>
              <h2 className="capitalize mb-6 hover:underline">
                {product?.brand}
              </h2>
            </Link>
            <div className="mb-6">
              <p className="capitalize text-lg font-extralight">
                {product?.title}
              </p>
              <p className="text-xl font-bold">
                {t("price-from")}{" "}
                <Price
                  className="text-xl"
                  value={product?.minVariantTargetPrice || 0}
                />
              </p>
            </div>
            <p className="text-secondary-gray6 my-6 max-md:text-sm">
              Rif. {selectedVariant?.ean} | {selectedVariant?.color.name}
            </p>
            <div className="flex items-center gap-[20px] max-w-[600px]">
              <Button
                onClick={goToOfferPage}
                className="flex-grow"
                disabled={!selectedSize}
              >
                {t("make-price")}
              </Button>
              {canShare() && (
                <button aria-label="share product" onClick={onShare}>
                  <Icon
                    icon="Share"
                    className="w-[46px] h-[46px] bg-secondary-gray2 dark:bg-primary-white rounded-full p-[12px] stroke-primary-black"
                  />
                </button>
              )}
              <Wishlist product={product} session={session} />
            </div>
            {productContent}
          </div>
        </div>
      </div>
    );
  }

  /* MOBILE */
  return (
    <div>
      <div className="relative">
        <Link
          className="[&_button]:h-8"
          href={breadcrumbItems?.at(-2)?.href || ""}
        >
          <IconButton
            variant="underlineReverse"
            icon="ArrowCircle"
            className="mb-[40px] max-md:mb-4"
          >
            {breadcrumbItems?.at(-2)?.text}
          </IconButton>
        </Link>
        {selectedVariant && selectedVariant.imgUrl ? (
          <Carousel
            className="rounded-[20px] bg-primary-white"
            autoplay={false}
            orientation="vertical"
          >
            <CarouselContent className="h-[680px] max-md:h-[calc(100dvh-60px-180px-var(--mobile-header-height))]">
              {selectedVariant.imgUrl.map((img: any, i: number) => {
                return (
                  <CarouselItem
                    key={i}
                    className="relative flex items-center justify-center bg-center bg-contain bg-no-repeat"
                    style={{ backgroundImage: `url(${img})` }}
                  ></CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        ) : selectedVariant?.catalogImgUrl ? (
          <div className="relative w-[100%] aspect-[16/10] overflow-hidden rounded-lg shadow-catalog-image dark:shadow-none">
            <Image
              data-loaded="false"
              className="w-[100%] object-cover data-[loaded=false]:animate-pulse data-[loaded=false]:bg-secondary-gray3/100 dark:data-[loaded=false]:bg-secondary-gray3/10"
              src={selectedVariant.catalogImgUrl}
              fill
              sizes="(min-width: 768px) 50vw, 100vw"
              style={{
                objectFit: "cover",
              }}
              onLoad={(event) => {
                event.currentTarget.setAttribute("data-loaded", "true");
              }}
              alt={product?.title}
            />
          </div>
        ) : (
          <div></div>
        )}
      </div>
      <Drawer
        modal={false}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            setSelectedSize(selectedSize || variant.size);
          }
        }}
        open={drawerOpen}
      >
        <DrawerTrigger
          onClick={(e: any) => {
            setDrawerOpen(true);
            e.preventDefault();
            e.stopPropagation();
            if (isMobilePreventForward()) {
              mobileForceSelectSize();
            }
          }}
        >
          <div
            {...handlers}
            className={`drop-shadow-product-drawer px-6 pt-4 text-left w-full fixed bottom-[79px] rounded-t-[40px] left-0 bg-primary-white text-primary-black`}
          >
            <div className="flex flex-col items-center">
              <Icon
                icon="DrawerArrow"
                className="*:fill-secondary-gray1 [&_svg]:animate-bounce-fill"
              />
            </div>
            <h1
              className={`text-lg font-semibold line-clamp-2 pb-[2px] mt-4 leading-none`}
            >
              {product?.title}
            </h1>
          </div>
        </DrawerTrigger>
        <DrawerContent className=" text-primary-black mb-[100px] top-[0px] h-[calc(100%-var(--mobile-header-height))] mt-[var(--mobile-header-height)]">
          <div className="bg-primary-white rounded-t-[40px] relative h-full">
            <div className="relative bg-primary-white w-[100vw] z-10 p-4 rounded-t-[40px] drop-shadow-product-drawer">
              <DrawerTrigger className="w-full">
                <div className="flex flex-col items-center">
                  <Icon
                    icon="DrawerArrow"
                    className="*:fill-secondary-gray1 rotate-180"
                  />
                </div>
              </DrawerTrigger>
              <DrawerTitle className="text-lg font-semibold my-4"></DrawerTitle>
            </div>
            <div
              ref={drawerRef}
              className="w-[100vw] p-8 overflow-y-auto h-[calc(100%-77px)] pb-[100px]"
            >
              <Link href={brandSearchUrl(product?.brand)}>
                <div className="capitalize text-lg font-semibold mb-4 leading-6 hover:underline">
                  {product?.brand}
                </div>
              </Link>
              <div className="text-base font-extralight leading-none">
                {product?.title}
              </div>
              <div className="text-lg font-bold">
                {t("price-from")}{" "}
                <Price
                  className="text-lg"
                  value={product?.minVariantTargetPrice || 0}
                />
              </div>
              <div className="max-md:max-w-[100vw]">{productContent}</div>
              <div className="flex justify-center mt-6">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    scrollDrawerTo();
                  }}
                  className="inline-block"
                >
                  <span className="flex items-center justify-center gap-1 text-primary-black text-sm font-medium border-b border-primary-black">
                    <span>{t("scroll-to-top")}</span>
                    <Icon
                      icon="Arrow"
                      className="w-2 h-2 ml-1 [&_path]:stroke-[3] rounded-full stroke-primary-magenta -rotate-90"
                    />
                  </span>
                </a>
              </div>
              <div className="my-[30px] -mx-[40px] light max-md:max-w-[100vw]">
                <Footer />
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
      <div
        className={`px-6 py-0 w-full ${drawerOpen ? "drop-shadow-product-drawer" : ""} h-[80px] fixed bottom-0 left-0 flex items-center gap-5 bg-primary-white z-[10]`}
      >
        <div className="relative flex-grow">
          {isMobilePreventForward() ? (
            <Button
              onClick={() => {
                mobileForceSelectSize(true);
              }}
              variant="no-dark"
              className={cn(
                "z-20 text-sm w-full max-w-96 h-[40px] pointer-events-auto",
                {
                  "animate-pulse-light":
                    (!selectedSize && !sizesOpen) || !drawerOpen,
                },
              )}
            >
              {t("select-size")}
            </Button>
          ) : (
            <Button
              onClick={goToOfferPage}
              disabled={!selectedSize || !selectedVariant || !selectedColor}
              variant="no-dark"
              className="text-sm w-full bg-primary-magenta border-primary-magenta max-w-96 h-[40px] pointer-events-auto animate-pulse-light"
            >
              {t("make-price")}
            </Button>
          )}
        </div>
        <div className="flex gap-5">
          {canShare() && (
            <button onClick={onShare}>
              <Icon
                icon="Share"
                className="w-[46px] h-[46px] bg-secondary-gray2 rounded-full p-[12px] stroke-primary-black pointer-events-auto"
              />
            </button>
          )}
          <Wishlist product={product} session={session} />
        </div>
      </div>
    </div>
  );
}
